// cbe-core-keymap: the one (platform, chord) → EditorIntent | view motion table. Every view resolves hardware chords
// through `resolveKey`; drivers parse chords with `parseChord`. Donors: ProseMirror keeps bindings as a chord → command
// table per platform (prosemirror-commands/src/commands.ts:749-783, `pcBaseKeymap`/`macBaseKeymap`); Lexical resolves DOM
// keys in one place (lexical/src/LexicalEvents.ts:1598). A chord absent from the platform column resolves to nothing and
// is left to the platform.
import { checkItems } from './checklist'
import { toggleHeaderItem } from './toggle'
import type { EditorIntent } from './intents'
import type { CoreState } from './state'
import { locate, rows } from './tree-read'

export type Platform = 'mac' | 'pc' | 'ios' | 'android'
/** Chord syntax: modifiers `Mod`, `Ctrl`, `Meta`, `Alt`, `Shift`, then one `KeyboardEvent.key` value (`Mod+Shift+z`). */
export type KeyChord = string
/** View-only behaviour bound to a chord; views apply it in their own context, otherwise the key is left to the platform. */
export type ViewMotion = 'activate' | 'dismiss' | 'toggleLink' | 'toggleDisclosure' | 'toggleAllDisclosures' | 'transpose'
export type KeyBinding = { intent: EditorIntent } | { motion: ViewMotion }
export interface KeyStroke { key: string; code?: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean }

export class ChordSyntaxError extends Error {}

const MODIFIERS = ['Mod', 'Ctrl', 'Meta', 'Alt', 'Shift'] as const
type Modifier = typeof MODIFIERS[number]

const APPLE: readonly Platform[] = ['mac', 'ios']
const PC: readonly Platform[] = ['pc', 'android']

interface Entry {
  chord: KeyChord
  platforms?: readonly Platform[]
  when?: (state: CoreState) => boolean
  binding: KeyBinding
}

const inCode = (state: CoreState): boolean => {
  if (!state.selection) return false
  const entries = rows(state.blocks)
  return entries[locate(entries, state.selection.focus).row]?.block.type === 'pre'
}

/** The caret is in a check item's own text: Mod+Enter checks it (Easy's reading of Notion's Mod+Enter "modify the current
 * block", notion: unverified; Notion documents only the toggle open/close, N012). */
const inTodo = (state: CoreState): boolean => {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return false
  const entries = rows(state.blocks)
  const row = entries[locate(entries, selection.focus).row]
  return checkItems(state.blocks).some(item => item.row.block === row?.block)
}

/** The caret is in a code block or a text block of its own (not a list item's row, a table cell or a field): Mod+Enter
 * leaves it for a new paragraph after it. */
const exitable = (state: CoreState): boolean => {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return false
  const entries = rows(state.blocks)
  const row = entries[locate(entries, selection.focus).row]
  return row !== undefined && ['pre', 'p', 'h'].includes(row.block.type) && row.parents.at(-1)?.type !== 'li' &&
    !row.parents.some((parent) => ['table', 'question'].includes(parent.type))
}

const intent = (chord: KeyChord, value: EditorIntent, extra: Omit<Entry, 'chord' | 'binding'> = {}): Entry =>
  ({ chord, binding: { intent: value }, ...extra })

/** A caret chord and its `Shift+` extension. */
const caret = (chord: KeyChord, direction: 'backward' | 'forward' | 'up' | 'down',
  unit: 'character' | 'word' | 'document' | 'visualLine', platforms?: readonly Platform[]): Entry[] =>
  [false, true].map((extend) => intent(extend ? `Shift+${chord}` : chord,
    { type: 'moveCaret', direction, unit, extend }, platforms ? { platforms } : {}))

const table: readonly Entry[] = [
  { chord: 'Mod+Alt+t', binding: { motion: 'toggleAllDisclosures' } },
  { chord: 'Mod+Enter', binding: { motion: 'toggleDisclosure' }, when: state => toggleHeaderItem(state) !== undefined },
  intent('Mod+Enter', { type: 'toggleTodo' }, { when: inTodo }),
  // In code Mod+Enter adds a line, like Enter (Notion browser check imp27, rule nux/keys/code-mod-enter-exits).
  intent('Mod+Enter', { type: 'insertParagraph' }, { when: inCode }),
  intent('Mod+Enter', { type: 'exitBlock' }, { when: exitable }),
  intent('Enter', { type: 'insertParagraph' }),
  intent('Shift+Enter', { type: 'insertLineBreak' }),
  intent('Tab', { type: 'formatIndent' }),
  intent('Shift+Tab', { type: 'formatOutdent' }),
  intent('Backspace', { type: 'deleteContentBackward' }),
  intent('Delete', { type: 'deleteContentForward' }),
  intent('Alt+Backspace', { type: 'deleteWordBackward' }, { platforms: APPLE }),
  intent('Alt+Delete', { type: 'deleteWordForward' }, { platforms: APPLE }),
  intent('Ctrl+Backspace', { type: 'deleteWordBackward' }, { platforms: PC }),
  intent('Ctrl+Delete', { type: 'deleteWordForward' }, { platforms: PC }),
  intent('Meta+Backspace', { type: 'deleteSoftLineBackward' }, { platforms: APPLE }),
  intent('Meta+Delete', { type: 'deleteSoftLineForward' }, { platforms: APPLE }),
  intent('Ctrl+k', { type: 'deleteSoftLineForward' }, { platforms: APPLE }),
  { chord: 'Mod+k', binding: { motion: 'toggleLink' } },
  { chord: 'Ctrl+t', binding: { motion: 'transpose' } },
  intent('Mod+b', { type: 'formatBold' }),
  intent('Mod+i', { type: 'formatItalic' }),
  intent('Mod+u', { type: 'formatUnderline' }),
  // Notion keyboard shortcuts: strikethrough Mod+Shift+S, inline code Mod+E.
  intent('Mod+Shift+s', { type: 'formatStrikeThrough' }),
  intent('Mod+e', { type: 'formatCode' }),
  intent('Mod+a', { type: 'selectAll', stepwise: true }),
  intent('Mod+z', { type: 'historyUndo' }),
  intent('Mod+Shift+z', { type: 'historyRedo' }),
  intent('Mod+Shift+c', { type: 'formatBlock', block: 'paragraph' }, { when: inCode }),
  intent('Mod+Shift+c', { type: 'formatBlock', block: 'code' }),
  // Notion turn-into chords (keyboard-shortcuts help): 0 text, 1-3 headings, 4 to-do, 5 bullet, 6 numbered, 7 toggle, 8 code.
  ...(['paragraph', 'h1', 'h2', 'h3', 'check', 'bullet', 'number', 'toggle', 'code'] as const).map((block, digit) =>
    intent(`Mod+Alt+${digit}`, { type: 'formatBlock', block })),
  intent('Mod+Shift+ArrowUp', { type: 'moveBlock', direction: 'up' }),
  intent('Mod+Shift+ArrowDown', { type: 'moveBlock', direction: 'down' }),
  intent('Alt+ArrowUp', { type: 'moveCodeLines', direction: 'up' }, { when: inCode }),
  intent('Alt+ArrowDown', { type: 'moveCodeLines', direction: 'down' }, { when: inCode }),
  ...caret('ArrowLeft', 'backward', 'character'),
  ...caret('ArrowRight', 'forward', 'character'),
  ...caret('Alt+ArrowLeft', 'backward', 'word', APPLE),
  ...caret('Alt+ArrowRight', 'forward', 'word', APPLE),
  ...caret('Ctrl+ArrowLeft', 'backward', 'word', PC),
  ...caret('Ctrl+ArrowRight', 'forward', 'word', PC),
  ...caret('ArrowUp', 'up', 'visualLine'),
  ...caret('ArrowDown', 'down', 'visualLine'),
  ...caret('Home', 'backward', 'visualLine'),
  ...caret('End', 'forward', 'visualLine'),
  ...caret('Meta+ArrowLeft', 'backward', 'visualLine', APPLE),
  ...caret('Meta+ArrowRight', 'forward', 'visualLine', APPLE),
  ...caret('Ctrl+Home', 'backward', 'document'),
  ...caret('Ctrl+End', 'forward', 'document'),
  { chord: 'Escape', binding: { motion: 'dismiss' } },
  { chord: ' ', binding: { motion: 'activate' } },
]

/** Parses one chord; `Mod` resolves to Meta on mac/ios and Control on pc/android. */
export function parseChord(chord: KeyChord, platform: Platform): KeyStroke {
  const plus = chord === '+' || chord.endsWith('++')
  const parts = (plus ? chord.slice(0, -1) : chord).split('+')
  const key = plus ? '+' : parts.pop() ?? ''
  if (plus) parts.pop()
  const modifiers = new Set<Modifier>()
  for (const part of parts) {
    if (!MODIFIERS.includes(part as Modifier) || modifiers.has(part as Modifier)) {
      throw new ChordSyntaxError(`Invalid chord "${chord}": "${part}" is not one of ${MODIFIERS.join(', ')} (cbe-core-keymap)`)
    }
    modifiers.add(part as Modifier)
  }
  if (key === '') throw new ChordSyntaxError(`Invalid chord "${chord}": missing KeyboardEvent.key value (cbe-core-keymap)`)
  const apple = APPLE.includes(platform)
  return {
    key,
    metaKey: modifiers.has('Meta') || modifiers.has('Mod') && apple,
    ctrlKey: modifiers.has('Ctrl') || modifiers.has('Mod') && !apple,
    altKey: modifiers.has('Alt'),
    shiftKey: modifiers.has('Shift'),
  }
}

/** Resolves a hardware key stroke; `state` decides context-bound entries (caret inside code). */
export function resolveKey(platform: Platform, stroke: KeyStroke, state: CoreState | null): KeyBinding | undefined {
  const pressed = normalize(stroke)
  return table.find((entry) => (!entry.platforms || entry.platforms.includes(platform)) &&
    same(normalize(parseChord(entry.chord, platform)), pressed) &&
    (!entry.when || (state !== null && entry.when(state))))?.binding
}

export function resolveChord(platform: Platform, chord: KeyChord, state: CoreState | null): KeyBinding | undefined {
  return resolveKey(platform, parseChord(chord, platform), state)
}

/** A letter's case comes from Shift (`Mod+Shift+z` reports key `Z`); the table names letters in lower case. The fields are
 * read one by one: a live `KeyboardEvent` holds them as prototype getters, which a spread does not copy. */
function normalize(stroke: KeyStroke): KeyStroke {
  // Option+T produces † (Option+1 ¡) on US Mac keyboards; these documented chords name the physical key.
  const physical = stroke.altKey && (stroke.metaKey || stroke.ctrlKey)
  const key = physical && stroke.code === 'KeyT' ? 't'
    : physical && /^Digit\d$/u.test(stroke.code ?? '') ? (stroke.code ?? '').slice(5) : stroke.key
  return { key: key.length === 1 ? key.toLowerCase() : key, ctrlKey: stroke.ctrlKey,
    metaKey: stroke.metaKey, altKey: stroke.altKey, shiftKey: stroke.shiftKey }
}

function same(left: KeyStroke, right: KeyStroke): boolean {
  return left.key === right.key && left.ctrlKey === right.ctrlKey && left.metaKey === right.metaKey &&
    left.altKey === right.altKey && left.shiftKey === right.shiftKey
}
